import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Search as SearchIcon, X, Navigation, Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { fetchEvents, fetchCalendarEvents } from "../lib/events";
import { geocodePlace, fetchBoundary } from "../lib/eventApi";
import { searchEventsGeo, parseQuery, typePredicate, byTierThenSoonest, TIER } from "../lib/eventSearch";
import { EventCard } from "../components/EventCard";
import { AppMenu } from "../components/AppMenu";
import { CountrySelector } from "../components/CountrySelector";
import { AdSlot } from "../components/AdSlot";
import { Skeleton } from "../components/ui/skeleton";
import { distanceKm, formatDistance, getPosition, readCachedPosition } from "../lib/geo";
import { getNavState, saveNavState } from "../lib/navState";
import { useCountry } from "../context/CountryContext";

// Area radius (km) around a searched place. Mirrors the Calendar location filter.
const AREA_RADIUS_KM = 50;

const CATEGORIES = [
  "All",
  "Live Now",
  "Coming Up",
  "Cars",
  "Motorsport",
  "Motorcycles",
  "Shows",
  "Meets & Coffee",
  "Track Days",
];

export default function SearchPage() {
  const location = useLocation();
  const navKey = `search:${location.key}`;
  const savedNav = getNavState(navKey);
  const { country } = useCountry();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState(() => savedNav?.query ?? "");
  const [category, setCategory] = useState(() => savedNav?.category ?? "All");
  const [nearMe, setNearMe] = useState(() => savedNav?.nearMe ?? false);
  const [userPos, setUserPos] = useState(() => savedNav?.userPos ?? null);
  const [locating, setLocating] = useState(false);
  // Nearby fallback: when the typed text matches no event, it is geocoded within
  // the selected country so a postcode or village still surfaces events within
  // AREA_RADIUS_KM. `placeCenter` = { lat, lng } or null.
  const [placeCenter, setPlaceCenter] = useState(null);
  // Real boundary of the searched place { place, b } (county / state / city) so events
  // are matched by their map pin, not just the written address.
  const [boundary, setBoundary] = useState(null);
  const geoReq = useRef(0);
  // Incremental result rendering: mount a small first batch and reveal more on
  // scroll, so Search never mounts the whole result set at once (which stalled the
  // main thread and delayed the input/pills becoming responsive on mobile).
  const [visibleCount, setVisibleCount] = useState(() => savedNav?.visibleCount ?? 12);
  const loadMoreRef = useRef(null);
  const firstResetRun = useRef(true);

  const [totalCount, setTotalCount] = useState(null);
  // True once the progressive country load below has delivered every page.
  const [allComplete, setAllComplete] = useState(false);
  const handleNearMe = async () => {
    if (nearMe) {
      setNearMe(false);
      return;
    }
    if (userPos) {
      setNearMe(true);
      return;
    }
    const cached = readCachedPosition();
    if (cached) {
      // Cached-first: show Near Me results immediately, then refresh in the
      // background with a fresher live fix (silently keep cached if live fails).
      setUserPos(cached);
      setNearMe(true);
      getPosition()
        .then((pos) => setUserPos(pos))
        .catch(() => {
          /* keep cached position silently */
        });
      return;
    }
    setLocating(true);
    try {
      const pos = await getPosition();
      setUserPos(pos);
      setNearMe(true);
    } catch {
      toast.error("Location unavailable. Enable location to sort by distance.");
    } finally {
      setLocating(false);
    }
  };

  // Text search over the loaded events: name, organiser, venue, address (town/city),
  // postcode, category and description, graded by where the text matched (see
  // lib/eventSearch.js). Re-runs as the country's pages stream in.
  const parsedQuery = useMemo(() => parseQuery(query), [query]);
  const activeBoundary = boundary && boundary.place === parsedQuery.place ? boundary.b : null;
  const textSearch = useMemo(
    () => (query.trim() ? searchEventsGeo(all, query.trim(), country, activeBoundary) : null),
    [all, query, country, activeBoundary]
  );
  const noTextHits = !!textSearch && textSearch.hits.length === 0;

  // Look up the searched place's boundary (debounced) once the text has a place in it.
  useEffect(() => {
    const place = parsedQuery.place;
    if (place.length < 3) {
      setBoundary(null);
      return undefined;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const b = await fetchBoundary(place, country);
      if (!cancelled) setBoundary({ place, b });
    }, 500);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [parsedQuery, country]);

  // Only when nothing matches as text (e.g. a postcode, or a village with no listed
  // events) is the query geocoded — WITHIN the selected country, so "Perth" or
  // "Hamilton" resolve locally — to show events within AREA_RADIUS_KM of it.
  useEffect(() => {
    const q = query.trim();
    if (!noTextHits || !allComplete || q.length < 3) {
      geoReq.current += 1;
      setPlaceCenter(null);
      return;
    }
    const reqId = ++geoReq.current;
    const t = setTimeout(async () => {
      const center = await geocodePlace(parsedQuery.place || q, country);
      if (reqId === geoReq.current) setPlaceCenter(center);
    }, 600);
    return () => clearTimeout(t);
  }, [query, country, noTextHits, allComplete, parsedQuery]);

  const selectRows = useCallback((rows) => {
    const q = query.trim();
    const now = Date.now();
    const inCategory = (ev) => {
      if (category === "All") return true;
      // Reuse the existing RoadRnr LIVE NOW definition: start_at <= now < end_at.
      if (category === "Live Now") return !!ev.live;
      if (category === "Coming Up") {
        // Coming Up = live now OR starting within the next 30 days (rolling).
        const s = ev.start_iso ? new Date(ev.start_iso).getTime() : null;
        return !!ev.live || (s != null && s >= now && s <= now + 30 * 24 * 60 * 60 * 1000);
      }
      return ev.category === category;
    };
    const inArea = (ev) =>
      placeCenter &&
      typeof ev.latitude === "number" &&
      typeof ev.longitude === "number" &&
      distanceKm(placeCenter.lat, placeCenter.lng, ev.latitude, ev.longitude) <= AREA_RADIUS_KM;

    // Search text and the category chip work together: text matches first, then
    // the chip narrows them. No text = every event in the country.
    let hits;
    if (!q) hits = rows.map((ev) => ({ ev, tier: 0 }));
    else if (textSearch && textSearch.hits.length) hits = textSearch.hits;
    else {
      const typeOk = typePredicate(parsedQuery.types);
      hits = rows.filter((ev) => inArea(ev) && (!typeOk || typeOk(ev))).map((ev) => ({ ev, tier: TIER.NEARBY }));
    }
    hits = hits.filter((h) => inCategory(h.ev));

    // Near Me sorts by distance from the user (unchanged); a nearby-fallback search
    // sorts by distance from the searched place; a text search ranks by match
    // quality then soonest; no text = soonest first by each event's CURRENT/NEXT
    // occurrence (start_iso). One card per series.
    const useUser = nearMe && userPos;
    const ctr = useUser ? userPos : q && placeCenter && !(textSearch && textSearch.hits.length) ? placeCenter : null;
    if (ctr) {
      return hits
        .map(({ ev }) => ({
          ...ev,
          _distance:
            typeof ev.latitude === "number" && typeof ev.longitude === "number"
              ? distanceKm(ctr.lat, ctr.lng, ev.latitude, ev.longitude)
              : null,
        }))
        .sort((a, b) => (a._distance ?? Infinity) - (b._distance ?? Infinity));
    }
    if (q) return [...hits].sort(byTierThenSoonest).map((h) => h.ev);
    return hits
      .map((h) => h.ev)
      .sort((a, b) => {
        const ta = a.start_iso ? new Date(a.start_iso).getTime() : Infinity;
        const tb = b.start_iso ? new Date(b.start_iso).getTime() : Infinity;
        return ta - tb;
      });
  }, [query, category, nearMe, userPos, placeCenter, textSearch, parsedQuery]);

  const results = useMemo(() => selectRows(all), [all, selectRows]);
  // Progressive, Calendar-style load: stream every page for the country into `all`
  // (first 12 appear fast, the rest append in the background), then filter/sort
  // client-side. Matches the Calendar's scalability behaviour exactly.
  useEffect(() => {
    const controller = new AbortController();
    setAll([]);
    setAllComplete(false);
    setLoading(true);
    // Render progressively (pages of 200, so only a few steps per country) rather than
    // blocking on the whole list.
    const publish = (rows) => {
      if (controller.signal.aborted) return;
      setAll(rows);
      setLoading(false);
    };
    fetchCalendarEvents(country, publish, 0, [], controller.signal)
      .then((rows) => { publish(rows); if (!controller.signal.aborted) setAllComplete(true); })
      .catch(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [country]);
  // Full matching count for the active filters — loaded INSTANTLY as a single
  // count query for the common case (whole-country number), never climbing with the
  // progressive list. Only a text/Live/Coming query falls back to counting pages.
  useEffect(() => {
    const controller = new AbortController();
    const context = {};
    setTotalCount(null);
    (async () => {
      if (!query.trim() && !["Live Now", "Coming Up"].includes(category)) {
        const count = await fetchEvents(country, { category, context, countOnly: true, signal: controller.signal });
        if (!controller.signal.aborted) setTotalCount(count);
        return;
      }
      // Text / Live Now / Coming Up: counted from the full country list already
      // loaded for display (see shownCount) instead of re-fetching the country
      // 12 rows at a time, which left the count on "…" for a long time (support, Oct 2026).
    })().catch(() => {});
    return () => controller.abort();
  }, [country, query, category]);
  const simpleCount = !query.trim() && !["Live Now", "Coming Up"].includes(category);
  const shownCount = simpleCount ? totalCount : allComplete ? results.length : null;
  // Reset to the first batch whenever the result set changes — but NOT on the
  // initial mount, so a restored list depth (Back navigation) survives.
  useEffect(() => {
    if (firstResetRun.current) {
      firstResetRun.current = false;
      return;
    }
    setVisibleCount(12);
  }, [query, category, nearMe, userPos, placeCenter, country]);

  // Persist the current search/filter state + list depth for this history entry
  // so pressing Back rebuilds the same view (window scroll is restored by App.js).
  useEffect(() => {
    saveNavState(navKey, { query, category, nearMe, userPos, visibleCount });
  }, [navKey, query, category, nearMe, userPos, visibleCount]);

  // Reveal the next batch of 12 as the sentinel approaches the viewport.
  useEffect(() => {
    const el = loadMoreRef.current;
    if (loading || !el || visibleCount >= results.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) setVisibleCount((c) => c + 12);
      },
      { rootMargin: "400px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [results.length, visibleCount, loading]);

  return (
    <div data-testid="search-page" className="min-h-[100dvh] pb-28">
      <div className="sticky top-0 z-30 border-b border-white/10 bg-[#050505] px-5 pb-4 pt-6">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            Search
          </h1>
          <div className="flex items-center gap-2">
            <CountrySelector />
            <AppMenu />
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 rounded-full border border-white/10 bg-[#121212] px-5 py-3.5">
          <SearchIcon className="h-5 w-5 text-[#ff2a00]" />
          <input
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events, cities, categories"
            className="w-full bg-transparent text-base text-white placeholder:text-white/40 focus:outline-none md:text-sm"
          />
          {query && (
            <button
              data-testid="search-clear"
              onClick={() => setQuery("")}
              className="text-white/40 transition-colors hover:text-white"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="hide-scrollbar -mx-5 mt-4 flex gap-2 overflow-x-auto px-5">
          <button
            data-testid="filter-near-me"
            onClick={handleNearMe}
            disabled={locating}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
              nearMe
                ? "border-[#ff2a00] bg-[#ff2a00] text-white"
                : "border-white/10 bg-transparent text-white/60"
            }`}
          >
            {locating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Navigation className={`h-4 w-4 ${nearMe ? "fill-white" : ""}`} />
            )}
            Near Me
          </button>
          <div className="my-1 w-px shrink-0 bg-white/10" />
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 ${
                c === "Live Now"
                  ? category === c
                    ? "border-[#FFB000] bg-[#FFB000] text-black"
                    : "border-[#FFB000]/40 bg-transparent text-[#FFB000]"
                  : category === c
                  ? "border-[#ff2a00] bg-[#ff2a00] text-white"
                  : "border-white/10 bg-transparent text-white/60"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-md px-5 pt-5 md:max-w-7xl md:px-10">
        <p data-testid="results-count" className="text-sm text-white/40">
          {shownCount ?? "…"} {shownCount === 1 ? "event" : "events"}
          {nearMe && userPos
            ? " · sorted by distance"
            : placeCenter
            ? " · near your search"
            : ""}
        </p>
        {loading ? (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] md:gap-5">
            {[0, 1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-[240px] rounded-3xl bg-white/5" />
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="mt-16 text-center">
            <p className="font-display text-xl font-bold">No events found</p>
            <p className="mt-1 text-sm text-white/45">
              Try a different search or category.
            </p>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-[repeat(auto-fill,minmax(240px,1fr))] md:gap-5">
            {results.slice(0, visibleCount).reduce((acc, ev, i) => {
              acc.push(
                <EventCard
                  key={ev.id}
                  event={ev}
                  index={i}
                  distanceLabel={ev._distance != null ? formatDistance(ev._distance) : null}
                />
              );
              if ((i + 1) % 6 === 0) acc.push(<AdSlot key={`ad-${i}`} page="search" />);
              return acc;
            }, [])}
          </div>
        )}
        {results.length > 0 && visibleCount < results.length && (
          <div
            ref={loadMoreRef}
            data-testid="search-load-more"
            className="mt-6 flex min-h-[64px] items-center justify-center gap-2 text-sm font-medium text-white/50"
          >
            <ChevronDown className="h-4 w-4 animate-bounce text-[#ff2a00]" />
            More events below
          </div>
        )}
      </div>
    </div>
  );
}
